import React, { FC, useCallback, memo, useMemo } from 'react';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import { useLazyQuery } from '@apollo/client';
import FormikSelectableSearchDropdown from '@minecraft.formik-selectable-search-dropdown';
import ErrorMessage from '@minecraft.error-message';
import { gqlScheduleRequestType } from '@minecraft.graphql-types';
import { QUERY_SCHEDULES } from '../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { FilterData } from '../../shared/types';
import { generateSelectOptionsBasedOnField } from '../../shared/utils';
import { Label, StyledFormikSelectableSearchDropdown } from '../../shared/styles';

type Props = {
  name: FilterData['name'];
  projectId: number;
  label: FilterData['label'];
};

const Schedules: FC<Props> = ({ name, projectId, label }) => {
  const [getSchedules, { data, loading, error }] = useLazyQuery(QUERY_SCHEDULES, {
    variables: {
      projectId,
    },
  });

  const handleSchedulesFetch = useCallback((): void => {
    if (!data) {
      getSchedules();
    }
  }, [data]);

  const schedulesOptions = useMemo(() => {
    return {
      auditions: generateSelectOptionsBasedOnField(
        _sortBy(
          _filter(
            data?.searchSchedules?.schedules,
            (schedule) => !schedule.isCanceled && schedule.scheduleType === gqlScheduleRequestType.AUDITION
          ),
          'sortOrder'
        ),
        'name'
      ),
      callback: generateSelectOptionsBasedOnField(
        _sortBy(
          _filter(
            data?.searchSchedules?.schedules,
            (schedule) => !schedule.isCanceled && schedule.scheduleType !== gqlScheduleRequestType.AUDITION
          ),
          'sortOrder'
        ),
        'name'
      ),
    };
  }, [data?.searchSchedules?.schedules]);

  if (error) return <ErrorMessage error={error} />;

  const sections = [];

  if (schedulesOptions.auditions?.length) {
    sections.push(
      <FormikSelectableSearchDropdown.Section name="Auditions" isToggleable isOpen>
        {_map(schedulesOptions.auditions, (schedule) => (
          <FormikSelectableSearchDropdown.Option key={schedule.value} label={schedule.label} value={schedule.value} />
        ))}
      </FormikSelectableSearchDropdown.Section>
    );
  }

  if (schedulesOptions.callback?.length) {
    sections.push(
      <FormikSelectableSearchDropdown.Section name="Callbacks" isToggleable isOpen>
        {_map(schedulesOptions.callback, (schedule) => (
          <FormikSelectableSearchDropdown.Option key={schedule.value} label={schedule.label} value={schedule.value} />
        ))}
      </FormikSelectableSearchDropdown.Section>
    );
  }

  return (
    <>
      <Label>{label}</Label>
      <StyledFormikSelectableSearchDropdown
        saveAsOption
        name={name}
        isLoading={loading}
        onOptionsFetch={handleSchedulesFetch}
      >
        {sections}
      </StyledFormikSelectableSearchDropdown>
    </>
  );
};

export default memo(Schedules);
